import React from 'react';
import { ArrowRight, ClipboardList, Home, Activity, HeartPulse } from 'lucide-react';
import { trackCtaClick } from '../utils/analytics';
import Reveal from './Reveal';
import SmoothImage from './SmoothImage';

//
// "Doctor-Guided GLP-1 Care" — sits directly below the hero trust strip.
// Two-column layout per client mockup:
//   • Left  : hero composite image + "Your personalized care — included" card
//   • Right : eyebrow + headline + paired lifestyle/consult photos + CTA
//

const CARE_ITEMS = [
  {
    icon: ClipboardList,
    title: 'Weight Management Programme',
    body: 'AI-assisted assessment and doctor-led guidance.',
  },
  {
    icon: Home,
    title: 'Clinic Consultation',
    body: 'Connect with licensed partner clinics.',
  },
  {
    icon: Activity,
    title: 'Health Tracking',
    body: 'Monitor progress, lifestyle, and key health indicators.',
  },
  {
    icon: HeartPulse,
    title: 'Preventive Health',
    suffix: 'Coming Soon',
    body: 'Advanced labs and personalized health insights.',
  },
];

function CareItem({ index, icon: Icon, title, suffix, body }) {
  const isComingSoon = Boolean(suffix);
  return (
    <li className="care-item relative flex items-start gap-4 group
                   rounded-xl p-2 -m-2 transition-colors duration-300
                   hover:bg-brand-50/60">
      <span
        className={`relative shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full
                    bg-brand-600 text-white text-sm font-bold
                    transition-transform duration-300 group-hover:scale-110
                    ${isComingSoon ? 'care-badge-pulse' : ''}`}
      >
        {index}
      </span>
      <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl
                       bg-brand-50 text-brand-700
                       transition-colors duration-300 group-hover:bg-brand-100">
        <Icon
          size={20}
          strokeWidth={1.75}
          className={`transition-transform duration-300 group-hover:rotate-6
                      ${isComingSoon ? 'icon-breathe' : ''}`}
        />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm sm:text-base font-bold text-brand-700 flex items-center flex-wrap gap-x-2">
          <span>{title}</span>
          {isComingSoon && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand-700
                             border border-brand-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider
                             care-badge-shimmer">
              {suffix}
            </span>
          )}
        </h3>
        <p className="mt-0.5 text-xs sm:text-sm text-ink-600 leading-relaxed">
          {body}
        </p>
      </div>
      {/* Hover sweep — subtle teal underline that draws on hover */}
      <span aria-hidden="true" className="care-item__sweep" />
    </li>
  );
}

export default function DoctorGuidedGLP() {
  return (
    <section className="bg-white">
      <div className="container-x pt-12 pb-10 sm:pt-16 sm:pb-12 lg:pt-20 lg:pb-14">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">

          {/* ── LEFT COLUMN ─────────────────────────────────────────── */}
          <div className="space-y-5 sm:space-y-6">
            {/* Hero composite image (GLP-1 bottle + AI Assessment phone) — slides in from LEFT */}
            <Reveal direction="left" delay={0}>
              <SmoothImage
                src="/Sec 2 - Image 1.png"
                alt="GLP-1 medication and AI Assessment on phone"
                containerClassName="group aspect-[4/3] rounded-2xl overflow-hidden
                                    bg-gradient-to-br from-brand-50 via-white to-brand-50
                                    border border-ink-100 shadow-card card-hover-lift"
                className="absolute inset-0 w-full h-full object-cover
                           transition-transform duration-700 group-hover:scale-[1.03]"
                loading="eager"
              />
            </Reveal>

            {/* "Your personalized care — included" card — rises from BELOW */}
            <Reveal direction="up" delay={200}>
              <div className="rounded-2xl bg-white border border-ink-100 shadow-card p-5 sm:p-6">
                <h3 className="text-sm sm:text-base font-bold text-ink-900">
                  Your personalized care — includes:
                </h3>
                <ul className="relative mt-4 sm:mt-5 space-y-4 sm:space-y-5">
                  {/* Vertical timeline connector behind the numbered badges */}
                  <span
                    aria-hidden="true"
                    className="care-list__line absolute left-[15px] top-4 bottom-4 w-px"
                  />
                  {CARE_ITEMS.map((item, i) => (
                    <Reveal key={item.title} direction="left" delay={320 + i * 140}>
                      <CareItem index={i + 1} {...item} />
                    </Reveal>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* ── RIGHT COLUMN ────────────────────────────────────────── */}
          <div className="space-y-6 sm:space-y-8 lg:pl-4">
            {/* Eyebrow — drifts in from the RIGHT */}
            <Reveal direction="right" delay={0}>
              <p className="text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase text-brand-600">
                Doctor-guided Weight Management
              </p>
            </Reveal>

            {/* Headline — drifts in from the LEFT */}
            <Reveal direction="left" delay={120}>
              <h2 className="font-bold tracking-tight leading-[1.1]
                             text-3xl sm:text-4xl md:text-5xl text-ink-900">
                Doctor-led. AI-assisted.
                <br />
                Science-backed.
                <br />
                <span className="text-brand-500">Personalized</span> for Malaysians.
              </h2>
            </Reveal>

            {/* Paired lifestyle / consult photos — Image 2 from RIGHT, Image 3 from LEFT */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Reveal direction="right" delay={220}>
                <SmoothImage
                  src="/Sec 2 - Image 2.png"
                  alt="Patient living an active lifestyle"
                  containerClassName="aspect-square rounded-2xl overflow-hidden border border-ink-100 shadow-card card-hover-lift"
                  className="absolute inset-0 w-full h-full object-cover object-[center_10%]"
                />
              </Reveal>
              <Reveal direction="left" delay={340}>
                <SmoothImage
                  src="/Sec 2 - Image 3.png"
                  alt="Tele-consultation with a licensed doctor"
                  containerClassName="aspect-square rounded-2xl overflow-hidden border border-ink-100 shadow-card card-hover-lift"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </Reveal>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
              {/* Supporting paragraph — drifts from the RIGHT */}
              <Reveal direction="right" delay={420}>
                <p className="text-sm sm:text-base text-ink-600 leading-relaxed max-w-md">
                  Find a suitable Weight Management Programme  with confidence —
                  doctor-guided, science-backed, and designed around your
                  health goals and budget.
                </p>
              </Reveal>

              {/* CTA — scale-pops in last */}
              <Reveal direction="scale" delay={540}>
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClick({ page: 'home_glp_care', button_label: 'Learn More', destination: '#section-steps' });
                    const el = document.getElementById('section-steps');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="cta-sheen cta-pulse-glow group shrink-0 inline-flex items-center gap-2 font-semibold rounded-full
                             px-6 py-3 text-sm bg-brand-600 text-white hover:bg-brand-700
                             transition-colors shadow-card"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20
                                   transition-transform duration-300 group-hover:translate-x-0.5">
                    <ArrowRight size={14} />
                  </span>
                  Learn More
                </button>
              </Reveal>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
